import React, { useState, useEffect } from "react";
import { CommonImageInput, CommonInput } from "../common";
import { Category } from "../../types/interfaces";
import { CategoryType } from "../../constants/common";
import useAxios from "../../hooks/useAxios";


type ModalProps = {
  show: boolean;
  handleModalOpenClose: (show: boolean) => void;
  category?: Category | null;
  getListData: () => void;
};

type CategoryErrors = Partial<Record<keyof Category, string>>;

const CreateUpdateCategory: React.FC<ModalProps> = ({ show, handleModalOpenClose, category, getListData }) => {
  const axiosInstance = useAxios();
  
  const [errors, setErrors] = useState<Partial<CategoryErrors>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Category>({
    _id: "",
	cat_type: 1,
    title: "",
    description: "",
	display_image: "",
  });
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const [ displayimage, setDisplayImage ] = useState<File | null>(null);
  
  useEffect(() => {
    if (category) {
      setFormData((prev) => ({
        ...prev,
        _id: category._id ?? "",
		cat_type: category.cat_type ?? 1,
        title: category.title ?? "",
        description: category.description ?? "",
		display_image: category.displaypath ?? "",
      }));
    }
  }, [category]);

  /*Handle Form Element Value Changed*/
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsFormChanged(true);
  };

  /*Validate Form*/
  const validate = (): boolean => {
    const newErrors: CategoryErrors = {};
    if (!formData.title) {
      newErrors.title = "Title is required";
    }
	
	if (!formData.cat_type) {
      newErrors.cat_type = "Category type is required";
    }
	
    if (!formData.description) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /*Submit Form*/
  const handleSubmit = async () => {
    if (validate()) {
      setIsSubmitting(true);
      try {
		var form_data = await prepareSubmitData();  
        const response: any = await axiosInstance({
          url: "category/create-update",
          method: "POST",
		  data: form_data,
		  headers: {
			  'Content-Type': 'multipart/form-data',
		  },
		  timeout: 40000,
        });
        //console.log('create/update response', response.data)
        //on success close the modal
        if (!response.error) {
		  getListData();	
          handleClose();
        }
      } catch (error: any) {
        //console.error('Error in api request:', error);
        if (error?.response?.data?.type === "validation_error") {
          let errors = error.response.data.message.errors; // API returned an error message
          setErrors(errors);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  /*Preapare Submit Form Data*/
  const prepareSubmitData = async () => {
	const form_Data = new FormData();
	if(displayimage){
	  form_Data.append('display_image', displayimage);
    }
	form_Data.append('_id', formData._id);
	if (formData.cat_type !== undefined && formData.cat_type !== null) {
	  form_Data.append("cat_type", String(formData.cat_type));
	}
	form_Data.append('title', formData.title);
	form_Data.append('description', formData.description);
	return form_Data;
  }

  const handleClose = () => {
    const newErrors: Partial<CategoryErrors> = {};
    setErrors(newErrors);
    setFormData({ _id: "", cat_type: 1, title: "", description: "", display_image: "" });
    handleModalOpenClose(false);
  };

  return (
    <>
	  <div
        className="modal fade show"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        
		<div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable animate__animated animate__fadeInDown">
          <div className="modal-content position-relative">
            {/* Close button */}
            <button
              type="button"
              className="btn btn-icon btn-active-color-primary position-absolute"
              style={{ top: "10px", right: "10px", zIndex: 10 }}
              onClick={handleClose}
            >
              ✖
            </button>
			  <div className="modal-header">
				<h2>{category ? "Update category" : "Create category"}</h2>
			  </div>
			  <div className="modal-body mx-xl-5">
				<form className="form">
					<div className="card-body pt-0 mb-4">
					  <label className="required form-label">Thumbnail</label>
					  <br />
					  <div className="text-center mb-3">
						<CommonImageInput
						  fieldName="display_image"
						  defaultImage={formData.display_image !== '' ? formData.display_image : "/assets/media/svg/blank-image.svg"}
						  onImageChange={(file) => setDisplayImage(file)}
						/>
					  </div>
					  {errors.display_image && (
						  <span className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
							{errors.display_image}
						  </span>
					  )}
					  <div className="text-muted fs-7">
						Set the product thumbnail image. Only *.png, *.jpg and *.jpeg
						image files are accepted
					  </div>
					</div>
					<div className="">
					  <label className="required form-label">Category Type</label>
					  <select
						className={`form-select mb-2 ${errors.cat_type ? "is-invalid" : ""}`}
						name="cat_type"
						value={Number(formData.cat_type)}
						onChange={handleInputChange}
					  >
						<option value="">Set Category Type</option>
						{CategoryType.map((item) => 
						  (
						    <option key={item.value} value={item.value}>{item.name}</option>
						  )
						)}
					  </select>
					  {errors.cat_type && (
						  <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
							{typeof errors.cat_type === "string" ? errors.cat_type : ''}
						  </div>
					  )}
					  <div className="text-muted text-info fs-7">
						Set the category type.
					  </div>
					</div>
					<div className="">	
					  <label className="required fs-6 fw-semibold form-label mb-1">
						Title
					  </label>
					  <CommonInput
						type="text"
						name="title"
						placeholder="Enter title"
						value={formData.title}
						onChange={handleInputChange}
						error={errors.title}
						limit={50}
					  />
					</div>
					<div className="">				
					  <label className="required fs-6 fw-semibold form-label mb-1">
						Description
					  </label>
					  <CommonInput
						type="text"
						name="description"
						placeholder="Enter description"
						value={formData.description}
						onChange={handleInputChange}
						error={errors.description}
						className={`form-control form-control ${
						  errors.description ? "is-invalid" : ""
						}`}
					  />
					</div>
					<div className="border-top mb-2" />
					<div className="text-center">
						<button
						  type="button"
						  className="btn btn-light me-3"
						  onClick={handleClose}
						>
						  Cancel
						</button>
						{isSubmitting ? (
						  <button
							type="button"
							id="kt_modal_new_card_submit"
							className="btn btn-primary"
							disabled={true}
						  >
							<span
							  className="indicator-progress"
							  style={{ display: "block" }}
							>
							  Please wait...
							  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
							</span>
						  </button>
						) : (
						  <button
							type="button"
							id="kt_modal_new_card_submit"
							className="btn btn-primary"
							onClick={handleSubmit}
						  >
							<span className="indicator-label">Submit</span>
						  </button>
						)}
					</div>
				</form>
			  </div>
			</div>
         </div>
      </div>
	  <div className="modal-backdrop fade show" />
    </>
  );
};

export default CreateUpdateCategory;
